// src/merch/paint.js
//
// Freehand drawing for MerchMod — same flow as Tattune's paint tool:
// draw onto a temporary paint buffer, then "Add as layer" turns it into a
// normal, movable layer placed exactly where it was drawn.
//
// The buffer lives in the shared 2048px design square (same space as the
// composite), so strokes line up with layers and the print file exactly.
// Canvas objects aren't put in the valtio store (not proxy-friendly); the
// store only tracks settings + a rev counter that triggers redraws.

import { snapshot } from "valtio";
import { RopeStabilizer, widthForPressure, alphaBounds, buildFlattenedUV } from "../engine";
import { alphaForPressure } from "../engine/stroke";
import { merch, addLayerRaw } from "./store";
import { BLEED_MM } from "./products";

export const PAINT_SIZE = 2048;

// Rendering is incremental so it stays fast on iPad (Apple Pencil reports
// ~240×/s) and doesn't slow down over a long session:
//   committed — every finished stroke, baked once (rebuilt only on undo /
//               background fill / clear)
//   cur       — the stroke in progress; each pointer move draws only its
//               NEW segment (pen, eraser, spray) or the opaque shape for
//               marker / soft, which are then composited in one go
//   buffer    — what everyone else sees (committed + live stroke), updated
//               at most once per animation frame, and only where the
//               stroke is
// The soft brush feathers by downscaling + smooth upscaling instead of
// canvas shadow blur (very slow in Safari).
const mk = () => { const c = document.createElement("canvas"); c.width = c.height = PAINT_SIZE; return c; };
let buffer = null, committed = null, cur = null;
function getBuffer() { if (!buffer) buffer = mk(); return buffer; }
function getCommitted() { if (!committed) committed = mk(); return committed; }
function getCur() { if (!cur) cur = mk(); return cur; }

export function getPaintCanvas() {
  return merch.paint.strokes > 0 || current ? getBuffer() : null;
}

const strokes = [];   // draw order (background fills sit first)
const history = [];   // add order, for Undo
let current = null;   // stroke being drawn: { ...stroke, st: incremental state, bb: bounds }
const rope = new RopeStabilizer();

/* ---------- incremental stroke drawing ---------- */

const softBlur = (w) => Math.max(2, w * 0.45);
function padFor(stroke) {
  return stroke.brush === "soft" && !stroke.erase ? Math.ceil(softBlur(stroke.base * 1.5) * 2) + 4 : 4;
}
function growBB(stroke, x, y, w) {
  const m = w / 2 + padFor(stroke);
  const bb = stroke.bb || (stroke.bb = { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity });
  bb.x0 = Math.min(bb.x0, x - m); bb.y0 = Math.min(bb.y0, y - m);
  bb.x1 = Math.max(bb.x1, x + m); bb.y1 = Math.max(bb.y1, y + m);
}
function rectOf(bb) {
  const x = Math.max(0, Math.floor(bb.x0)), y = Math.max(0, Math.floor(bb.y0));
  const x1 = Math.min(PAINT_SIZE, Math.ceil(bb.x1)), y1 = Math.min(PAINT_SIZE, Math.ceil(bb.y1));
  return x1 > x && y1 > y ? { x, y, w: x1 - x, h: y1 - y } : null;
}
const brushOf = (s) => (s.erase ? "pen" : s.brush || "pen");

function stamp(ctx, x, y, w, color) {
  const r = w * 0.9;
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, color);
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
}

/** Draw points [st.i ..] of `stroke` onto ctx (the `cur` canvas). */
function drawNew(ctx, stroke, final = false) {
  const pts = stroke.points, st = stroke.st;
  const color = stroke.erase ? "#000" : stroke.color || "#111";
  const brush = brushOf(stroke);
  ctx.save();
  if (brush === "spray") {
    // pen pressure → opacity (a light touch mists, pressing builds colour)
    const flow = 0.14 * (stroke.opacity ?? 1);
    ctx.globalAlpha = flow * (pts[0].a ?? 1);
    if (st.i === 0) { stamp(ctx, pts[0].x, pts[0].y, pts[0].w, color); st.i = 1; st.carry = 0; }
    for (; st.i < pts.length; st.i++) {
      const a = pts[st.i - 1], b = pts[st.i];
      const d = Math.hypot(b.x - a.x, b.y - a.y);
      const step = Math.max(1, ((a.w + b.w) / 2) * 0.18);
      let t = step - st.carry;
      while (t <= d) {
        const k = t / d;
        ctx.globalAlpha = flow * ((a.a ?? 1) + ((b.a ?? 1) - (a.a ?? 1)) * k);
        stamp(ctx, a.x + (b.x - a.x) * k, a.y + (b.y - a.y) * k, a.w + (b.w - a.w) * k, color);
        t += step;
      }
      st.carry = d - (t - step);
    }
  } else {
    // pen / marker / soft / eraser: smoothed quadratic path, segment by segment
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    if (st.i === 0) { st.prevMid = pts[0]; st.i = 1; }
    for (; st.i < pts.length; st.i++) {
      const p = pts[st.i - 1], q = pts[st.i];
      const mid = { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 };
      ctx.beginPath();
      ctx.lineWidth = (p.w + q.w) / 2;
      ctx.moveTo(st.prevMid.x, st.prevMid.y);
      ctx.quadraticCurveTo(p.x, p.y, mid.x, mid.y);
      ctx.stroke();
      st.prevMid = mid;
    }
    if (final) {
      const last = pts[pts.length - 1];
      if (pts.length === 1) {
        ctx.beginPath();
        ctx.arc(last.x, last.y, last.w / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.lineWidth = last.w;
        ctx.moveTo(st.prevMid.x, st.prevMid.y);
        ctx.lineTo(last.x, last.y);
        ctx.stroke();
      }
    }
  }
  ctx.restore();
}

// Soft feather: halve the stroke region step by step, then double it back
// up step by step (a smoothing pyramid). Each step is a proper 2×2
// average, so the edge comes out as a smooth falloff with no blocks or
// stair-steps, and it's all plain drawImage calls, fast everywhere.
const pyr = [];
function pyrCanvas(i, w, h) {
  let c = pyr[i];
  if (!c) c = pyr[i] = document.createElement("canvas");
  if (c.width < w || c.height < h) { c.width = Math.max(c.width, w); c.height = Math.max(c.height, h); }
  const x = c.getContext("2d");
  x.imageSmoothingEnabled = true;
  x.imageSmoothingQuality = "high";
  return c;
}
function softBlurDraw(ctx, src, r, blur) {
  // one step less than the blur size: solid core, soft edge (like the old shadow blur)
  const levels = Math.max(1, Math.min(4, Math.round(Math.log2(Math.max(2, blur))) - 1));
  const lv = [];
  let from = src, fx = r.x, fy = r.y, fw = r.w, fh = r.h;
  for (let i = 0; i < levels; i++) {
    const w = Math.max(1, Math.ceil(fw / 2)), h = Math.max(1, Math.ceil(fh / 2));
    const c = pyrCanvas(i, w + 2, h + 2);
    const x = c.getContext("2d");
    x.clearRect(0, 0, c.width, c.height);
    x.drawImage(from, fx, fy, fw, fh, 1, 1, w, h); // 1px transparent margin: no edge smear
    lv.push({ c, w, h });
    from = c; fx = 1; fy = 1; fw = w; fh = h;
  }
  // back up the chain
  for (let i = levels - 2; i >= 0; i--) {
    const { c, w, h } = lv[i], n = lv[i + 1];
    const x = c.getContext("2d");
    x.clearRect(0, 0, c.width, c.height);
    x.drawImage(n.c, 1, 1, n.w, n.h, 1, 1, n.w * 2, n.h * 2);
    lv[i] = { c, w: Math.min(w, n.w * 2), h: Math.min(h, n.h * 2) };
  }
  const top = lv[0];
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(top.c, 1, 1, top.w, top.h, r.x, r.y, top.w * 2, top.h * 2);
}

/** Composite the `cur` canvas (region r) onto ctx the way the brush looks. */
function present(ctx, stroke, r) {
  const brush = brushOf(stroke);
  const c = getCur();
  ctx.save();
  ctx.globalCompositeOperation = stroke.erase ? "destination-out" : "source-over";
  const op = stroke.erase ? 1 : stroke.opacity ?? 1;
  if (brush === "marker") {
    ctx.globalAlpha = 0.55 * op; // whole stroke at once → no darker overlaps
    ctx.drawImage(c, r.x, r.y, r.w, r.h, r.x, r.y, r.w, r.h);
  } else if (brush === "soft") {
    ctx.beginPath();
    ctx.rect(r.x, r.y, r.w, r.h);
    ctx.clip();
    ctx.globalAlpha = op;
    softBlurDraw(ctx, c, r, softBlur(stroke.base));
  } else {
    ctx.drawImage(c, r.x, r.y, r.w, r.h, r.x, r.y, r.w, r.h);
  }
  ctx.restore();
}

/** Fully render one stroke / fill onto ctx (bake, rebuild). */
function renderStroke(ctx, stroke) {
  if (stroke.fill) { ctx.drawImage(stroke.canvas, stroke.x, stroke.y); return; }
  if (!stroke.points?.length) return;
  const r = rectOf(stroke.bb);
  if (!r) return;
  const cctx = getCur().getContext("2d");
  cctx.clearRect(r.x, r.y, r.w, r.h);
  stroke.st = { i: 0 };
  drawNew(cctx, stroke, true);
  present(ctx, stroke, r);
  cctx.clearRect(r.x, r.y, r.w, r.h);
}

/** Buffer = committed (+ live stroke) in region r (whole canvas if omitted). */
function composeBuffer(r) {
  const b = getBuffer().getContext("2d");
  r = r || { x: 0, y: 0, w: PAINT_SIZE, h: PAINT_SIZE };
  b.clearRect(r.x, r.y, r.w, r.h);
  b.drawImage(getCommitted(), r.x, r.y, r.w, r.h, r.x, r.y, r.w, r.h);
  if (current) present(b, current, r);
}

function rebuildCommitted() {
  const ctx = getCommitted().getContext("2d");
  ctx.clearRect(0, 0, PAINT_SIZE, PAINT_SIZE);
  for (const s of strokes) renderStroke(ctx, s);
  composeBuffer();
}

// one screen update per animation frame, however fast the pen reports
let frame = 0;
function schedule() {
  if (frame) return;
  frame = requestAnimationFrame(() => {
    frame = 0;
    if (current?.bb) composeBuffer(rectOf(current.bb));
    merch.paint.rev++;
  });
}

function touch() {
  merch.paint.strokes = strokes.length;
  merch.paint.rev++;
}

/** mm → paint-buffer px for the current sticker. */
function mmToPx(mm, sticker, area) {
  return (mm / sticker.widthMm) * (area.x1 - area.x0) * PAINT_SIZE;
}

// ---- stroke lifecycle (x, y in paint-buffer px) ----

// Smooth level 1–10 → rope length in SCREEN px (3–40), converted to buffer
// px so it feels the same at any zoom. paintPerScreen = buffer px per
// screen px, from the editor.
export function beginStroke(x, y, e, sticker, area, paintPerScreen = 1) {
  const level = merch.paint.smoothLevel ?? 5;
  rope.enabled = merch.paint.smooth && level > 0;
  rope.rope = (3 + (level - 1) * 4.1) * paintPerScreen;
  rope.strength = 0.25 + level * 0.03;
  rope.reset();
  const base = Math.max(1, mmToPx(merch.paint.sizeMm, sticker, area));
  const p = rope.push(x, y);
  current = {
    color: merch.paint.color,
    erase: merch.paint.erase,
    brush: merch.paint.brush || "pen",
    opacity: (merch.paint.brush === "soft" || merch.paint.brush === "marker" || merch.paint.brush === "spray") ? (merch.paint.opacity ?? 100) / 100 : 1,
    base,
    points: [],
    st: { i: 0 },
    bb: null,
  };
  addPoint(p, widthForPressure(base, e), alphaForPressure(e));
}

function addPoint(p, w, a = 1) {
  current.points.push({ ...p, w, a });
  growBB(current, p.x, p.y, w);
  drawNew(getCur().getContext("2d"), current);
  schedule();
}

export function extendStroke(x, y, e) {
  if (!current) return;
  const p = rope.push(x, y);
  const last = current.points[current.points.length - 1];
  if (Math.hypot(p.x - last.x, p.y - last.y) < 0.5) return;
  addPoint(p, widthForPressure(current.base, e), alphaForPressure(e));
}

export function endStroke() {
  if (!current) return;
  const s = current;
  const lastW = s.points[s.points.length - 1].w;
  const lastA = s.points[s.points.length - 1].a ?? 1;
  for (const p of rope.catchUp()) { s.points.push({ ...p, w: lastW, a: lastA }); growBB(s, p.x, p.y, lastW); }
  const cctx = getCur().getContext("2d");
  drawNew(cctx, s, true);
  const r = rectOf(s.bb);
  current = null;
  if (r) {
    present(getCommitted().getContext("2d"), s, r); // bake once
    cctx.clearRect(r.x, r.y, r.w, r.h);
    composeBuffer(r);
  }
  delete s.st;
  strokes.push(s);
  history.push(s);
  touch();
}

/** Throw away the stroke in progress (a second finger landed: the
 *  touch was the start of a pinch/pan, not a line). */
export function cancelStroke() {
  if (!current) return;
  const r = current.bb ? rectOf(current.bb) : null;
  current = null;
  if (r) {
    getCur().getContext("2d").clearRect(r.x, r.y, r.w, r.h);
    composeBuffer(r);
  }
  touch();
}

/* ---------------- paint bucket ---------------- */

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

let filling = false;

/**
 * Flood-fill at (x, y) (paint-buffer px) using what's VISIBLE on this
 * sticker — every layer plus the current drawing — as the boundaries.
 * Tapping empty background fills the whole background, bleed included.
 * The fill becomes an undoable "stroke" in the drawing.
 */
export async function fillAt(x, y, sticker, area) {
  if (filling) return;
  filling = true;
  try {
    const S = PAINT_SIZE;
    // region: print area + bleed (fills never leak past what gets printed)
    const bx = (BLEED_MM / sticker.widthMm) * (area.x1 - area.x0);
    const by = (BLEED_MM / sticker.heightMm) * (area.y1 - area.y0);
    const rx0 = Math.max(0, Math.floor((area.x0 - bx) * S)), ry0 = Math.max(0, Math.floor((area.y0 - by) * S));
    const rx1 = Math.min(S, Math.ceil((area.x1 + bx) * S)), ry1 = Math.min(S, Math.ceil((area.y1 + by) * S));
    const W = rx1 - rx0, H = ry1 - ry0;
    const sx = Math.round(x) - rx0, sy = Math.round(y) - ry0;
    if (sx < 0 || sy < 0 || sx >= W || sy >= H) return;

    // what's visible: layers + current drawing
    const comp = await buildFlattenedUV(snapshot(merch).layers, { size: S, edgeClip: 0.05 });
    const cctx = comp.getContext("2d");
    if (strokes.length) cctx.drawImage(getCommitted(), 0, 0);
    const src = cctx.getImageData(rx0, ry0, W, H).data;

    // scanline flood fill with tolerance (alpha counts: empty ≠ white)
    const i0 = (sy * W + sx) * 4;
    const t = [src[i0], src[i0 + 1], src[i0 + 2], src[i0 + 3]];
    const TOL = 48;
    const same = (p) => {
      const i = p * 4;
      if (t[3] < 16) return src[i + 3] < 40; // background: stay in empty space
      return src[i + 3] > 40 &&
        Math.abs(src[i] - t[0]) <= TOL && Math.abs(src[i + 1] - t[1]) <= TOL && Math.abs(src[i + 2] - t[2]) <= TOL;
    };
    const mask = new Uint8Array(W * H);
    const stack = [sx, sy];
    while (stack.length) {
      const py = stack.pop(), px0 = stack.pop();
      let px = px0;
      while (px >= 0 && !mask[py * W + px] && same(py * W + px)) px--;
      px++;
      let up = false, down = false;
      while (px < W && !mask[py * W + px] && same(py * W + px)) {
        mask[py * W + px] = 1;
        if (py > 0) {
          const u = (py - 1) * W + px;
          if (!mask[u] && same(u)) { if (!up) { stack.push(px, py - 1); up = true; } } else up = false;
        }
        if (py < H - 1) {
          const d = (py + 1) * W + px;
          if (!mask[d] && same(d)) { if (!down) { stack.push(px, py + 1); down = true; } } else down = false;
        }
        px++;
      }
    }

    // grow 1px under anti-aliased edges so no halo shows between line and fill
    const grown = mask.slice();
    for (let yy = 0; yy < H; yy++) for (let xx = 0; xx < W; xx++) {
      if (mask[yy * W + xx]) continue;
      if ((xx > 0 && mask[yy * W + xx - 1]) || (xx < W - 1 && mask[yy * W + xx + 1]) ||
          (yy > 0 && mask[(yy - 1) * W + xx]) || (yy < H - 1 && mask[(yy + 1) * W + xx])) grown[yy * W + xx] = 1;
    }

    // bitmap of just the filled bbox
    let fx0 = W, fy0 = H, fx1 = -1, fy1 = -1;
    for (let yy = 0; yy < H; yy++) for (let xx = 0; xx < W; xx++) {
      if (!grown[yy * W + xx]) continue;
      if (xx < fx0) fx0 = xx; if (xx > fx1) fx1 = xx; if (yy < fy0) fy0 = yy; if (yy > fy1) fy1 = yy;
    }
    if (fx1 < 0) return;
    const fw = fx1 - fx0 + 1, fh = fy1 - fy0 + 1;
    const out = document.createElement("canvas");
    out.width = fw; out.height = fh;
    const octx = out.getContext("2d");
    const img = octx.createImageData(fw, fh);
    const [r, g, b] = hexToRgb(merch.paint.color);
    for (let yy = 0; yy < fh; yy++) for (let xx = 0; xx < fw; xx++) {
      if (!grown[(yy + fy0) * W + xx + fx0]) continue;
      const o = (yy * fw + xx) * 4;
      img.data[o] = r; img.data[o + 1] = g; img.data[o + 2] = b; img.data[o + 3] = 255;
    }
    octx.putImageData(img, 0, 0);
    // background fills go UNDER existing strokes; shape fills on top
    const fill = { fill: true, canvas: out, x: rx0 + fx0, y: ry0 + fy0 };
    history.push(fill);
    if (t[3] < 16) { strokes.unshift(fill); rebuildCommitted(); } // under everything
    else { strokes.push(fill); renderStroke(getCommitted().getContext("2d"), fill); composeBuffer(); }
    touch();
  } finally {
    filling = false;
  }
}

export function undoStroke() {
  const last = history.pop();
  const i = strokes.lastIndexOf(last);
  if (i >= 0) strokes.splice(i, 1); else strokes.pop();
  rebuildCommitted();
  touch();
}

export function clearPaint() {
  strokes.length = 0;
  history.length = 0;
  current = null;
  getCommitted().getContext("2d").clearRect(0, 0, PAINT_SIZE, PAINT_SIZE);
  getCur().getContext("2d").clearRect(0, 0, PAINT_SIZE, PAINT_SIZE);
  composeBuffer();
  touch();
}

/** Convert the paint buffer into a normal layer, placed where drawn. */
export function commitPaintAsLayer() {
  const c = getBuffer();
  const b = alphaBounds(c);
  if (!b) return null;
  const pad = 4;
  const x0 = Math.max(0, b.x0 - pad), y0 = Math.max(0, b.y0 - pad);
  const x1 = Math.min(PAINT_SIZE, b.x1 + pad), y1 = Math.min(PAINT_SIZE, b.y1 + pad);
  const W = x1 - x0, H = y1 - y0;
  const crop = document.createElement("canvas");
  crop.width = W;
  crop.height = H;
  crop.getContext("2d").drawImage(c, x0, y0, W, H, 0, 0, W, H);

  // Engine layer placement: image is contained into the square, then
  // scaled by `scale` about the centre (tx, ty). To land at native size:
  // scale = long edge / square size.
  const scale = Math.max(W, H) / PAINT_SIZE;
  const id = addLayerRaw({
    name: "Drawing",
    url: crop.toDataURL("image/png"),
    kind: "paint",
    transform: {
      tx: (x0 + W / 2) / PAINT_SIZE,
      ty: (y0 + H / 2) / PAINT_SIZE,
      scale,
      rot: 0,
    },
  });
  clearPaint();
  return id;
}
